/**
 * Tolling Contracts API Module
 */

import { type RequestFunction } from './types'
import type {
  TollingContract,
  TollingContractDetail,
  TollingContractCreateInput,
  TollingContractUpdateInput,
  TollingContractProgress,
  TollingContainerAssignment,
  TollingAssignContainersInput,
  PaginatedTollingContracts,
  PaginatedTollingContainerAssignments,
  PaginatedTollingContractSocks,
  PaginatedTollingContractBatches,
  PaginatedTollingContractJars,
  FilterParams,
} from './types'

export class TollingApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // CONTRACTS
  // ============================================================================

  async getContracts(params?: FilterParams & { status?: string; search?: string; page?: number; page_size?: number }): Promise<PaginatedTollingContracts> {
    return this.request('/api/tolling/contracts/', { params })
  }

  async getContract(uuid: string): Promise<TollingContractDetail> {
    return this.request(`/api/tolling/contracts/${uuid}/`)
  }

  async createContract(data: TollingContractCreateInput): Promise<TollingContract> {
    return this.request('/api/tolling/contracts/', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateContract(uuid: string, data: TollingContractUpdateInput): Promise<TollingContract> {
    return this.request(`/api/tolling/contracts/${uuid}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // PROGRESS
  // ============================================================================

  async getContractProgress(uuid: string): Promise<TollingContractProgress> {
    return this.request(`/api/tolling/contracts/${uuid}/progress/`)
  }

  // ============================================================================
  // HEMP RECORDS (CONTAINER ASSIGNMENTS)
  // ============================================================================

  async getContractContainers(uuid: string, params?: FilterParams & { page?: number; page_size?: number }): Promise<PaginatedTollingContainerAssignments> {
    return this.request(`/api/tolling/contracts/${uuid}/containers/`, { params })
  }

  async assignContainers(uuid: string, data: TollingAssignContainersInput): Promise<TollingContainerAssignment[]> {
    return this.request(`/api/tolling/contracts/${uuid}/containers/assign/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async unassignContainer(uuid: string, assignmentId: number): Promise<void> {
    return this.request(`/api/tolling/contracts/${uuid}/containers/${assignmentId}/`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // SOCKS
  // ============================================================================

  async getContractSocks(uuid: string, params?: FilterParams & { page?: number; page_size?: number }): Promise<PaginatedTollingContractSocks> {
    return this.request(`/api/tolling/contracts/${uuid}/socks/`, { params })
  }

  // ============================================================================
  // BATCHES
  // ============================================================================

  async getContractBatches(uuid: string, params?: FilterParams & { page?: number; page_size?: number }): Promise<PaginatedTollingContractBatches> {
    return this.request(`/api/tolling/contracts/${uuid}/batches/`, { params })
  }

  // ============================================================================
  // JARS (STORAGE)
  // ============================================================================

  async getContractJars(uuid: string, params?: FilterParams & { page?: number; page_size?: number }): Promise<PaginatedTollingContractJars> {
    return this.request(`/api/tolling/contracts/${uuid}/jars/`, { params })
  }
}
